import axiosObject from './axios';
import { makeRequest, baseURL } from './https';
import { parseContentDispositionFilename } from '../utils/download.utils';
import { Organization, AddOrganization, UpdateOrganizationRequest, OrganizationAccountantsResponse } from '../types/organization.type';
import {
  OrganizationRatingStatusResponse,
  OrganizationRatingResponse,
  SubmitOrganizationRatingRequest,
  AdminOrganizationRatingsResponse,
  AdminOrganizationRatingsParams,
  PendingMeetingRating,
  DidNotAttendResponse,
} from '../types/organization-rating.type';

const LIST_FRESH_MS = 20_000;
const DETAIL_FRESH_MS = 20_000;

const listCache = new Map<string, { at: number; data: any }>();
const listInflight = new Map<string, Promise<any>>();
const detailCache = new Map<string, { at: number; data: any }>();
const detailInflight = new Map<string, Promise<any>>();

function readFresh<T>(cache: Map<string, { at: number; data: any }>, key: string, maxAge: number): T | null {
  const entry = cache.get(key);
  if (!entry || Date.now() - entry.at >= maxAge) return null;
  return entry.data as T;
}

export function invalidateOrganizationListCache() {
  listCache.clear();
}

function invalidateOrganizationDetail(organizationId?: string) {
  if (organizationId) {
    detailCache.delete(organizationId);
    return;
  }
  detailCache.clear();
}

function invalidateOrganizationWrites(organizationId?: string) {
  invalidateOrganizationListCache();
  invalidateOrganizationDetail(organizationId);
}

export interface OrganizationExportParams {
  search?: string;
  plan_code?: string;
  sector?: string;
  monthly_invoices_count?: string;
  accounting_software?: string;
  service_status?: string;
  accountant_id?: string;
  subscription_started_month?: string;
  subscription_expires_month?: string;
}

export const exportOrganizations = async (
  params: OrganizationExportParams,
): Promise<{ blob: Blob; filename: string }> => {
  const response = await axiosObject({
    url: `${baseURL}/organization/export`,
    method: 'get',
    params,
    responseType: 'blob',
  });
  const filename = parseContentDispositionFilename(response.headers['content-disposition'])
    || 'organizations.xlsx';
  return { blob: response.data as Blob, filename };
};

export const getOrganizations = (params?: any) => {
  const key = JSON.stringify(params ?? {});
  const cached = readFresh<any>(listCache, key, LIST_FRESH_MS);
  if (cached) return Promise.resolve(cached);
  const pending = listInflight.get(key);
  if (pending) return pending;

  const request = makeRequest({
    url: '/organization',
    method: 'get',
    params,
  }).then((data) => {
    listCache.set(key, { at: Date.now(), data });
    return data;
  }).finally(() => {
    listInflight.delete(key);
  });

  listInflight.set(key, request);
  return request;
};

export const getOrganization = (organizationId: string) => {
  const cached = readFresh<any>(detailCache, organizationId, DETAIL_FRESH_MS);
  if (cached) return Promise.resolve(cached);
  const pending = detailInflight.get(organizationId);
  if (pending) return pending;

  const request = makeRequest({
    url: `/organization/${organizationId}`,
    method: 'get',
  }).then((data) => {
    detailCache.set(organizationId, { at: Date.now(), data });
    return data;
  }).finally(() => {
    detailInflight.delete(organizationId);
  });

  detailInflight.set(organizationId, request);
  return request;
};

export const acceptOrganizationTerms = async (organizationId: string): Promise<Organization> => {
  const updated = await makeRequest({
    url: `/organization/${organizationId}/accept-terms`,
    method: 'post',
    data: { consent: true },
  });
  invalidateOrganizationWrites(organizationId);
  return updated;
};

export const getOrganizationAccountants = (organizationId: string): Promise<OrganizationAccountantsResponse> => {
  return makeRequest({
    url: `/organization/${organizationId}/accountants`,
    method: 'get',
  });
};

export const addOrganization = async (data: AddOrganization) => {
  const created = await makeRequest({
    url: '/organization',
    method: 'post',
    data,
  });
  invalidateOrganizationWrites();
  return created;
};

export const updateOrganization = async (id: string, data: UpdateOrganizationRequest): Promise<Organization> => {
  const updated = await makeRequest({
    url: `/organization/${id}`,
    method: 'put',
    data,
  });
  invalidateOrganizationWrites(id);
  return updated;
};

export const deleteOrganization = async (id: string): Promise<Organization> => {
  const deleted = await makeRequest({
    url: `/organization/${id}`,
    method: 'delete',
  });
  invalidateOrganizationWrites(id);
  return deleted;
};

export const updateOrganizationReportingType = async (
  organizationId: string,
  reportingType: 'monthly' | 'quarterly'
): Promise<Organization> => {
  const updated = await makeRequest({
    url: `/organization/${organizationId}/reporting-type`,
    method: 'patch',
    data: {
      reporting_type: reportingType
    },
  });
  invalidateOrganizationWrites(organizationId);
  return updated;
};

export const getOrganizationsWithUnansweredStats = () => {
  return makeRequest({
    url: '/organization',
    method: 'get',
    params: {
      with_unanswered_stats: true
    }
  });
};

export const getOrganizationRatingStatus = (
  organizationId: string,
): Promise<OrganizationRatingStatusResponse> => {
  return makeRequest({
    url: `/organization/${organizationId}/rating/status`,
    method: 'get',
  });
};

export const submitOrganizationRating = (
  organizationId: string,
  data: SubmitOrganizationRatingRequest,
): Promise<OrganizationRatingResponse> => {
  return makeRequest({
    url: `/organization/${organizationId}/rating`,
    method: 'post',
    data,
  });
};

export const getPendingMeetingRatings = (
  organizationId: string,
): Promise<PendingMeetingRating[]> => {
  return makeRequest({
    url: `/organization/${organizationId}/meetings/pending-ratings`,
    method: 'get',
  });
};

export const submitMeetingRating = (
  organizationId: string,
  meetingId: string,
  data: SubmitOrganizationRatingRequest,
): Promise<OrganizationRatingResponse> => {
  return makeRequest({
    url: `/organization/${organizationId}/meetings/${meetingId}/rating`,
    method: 'post',
    data,
  });
};

export const markMeetingDidNotAttend = (
  organizationId: string,
  meetingId: string,
): Promise<DidNotAttendResponse> => {
  return makeRequest({
    url: `/organization/${organizationId}/meetings/${meetingId}/did-not-attend`,
    method: 'post',
  });
};

export const getAdminOrganizationRatings = (
  params?: AdminOrganizationRatingsParams,
): Promise<AdminOrganizationRatingsResponse> => {
  return makeRequest({
    url: '/admin/organization-ratings',
    method: 'get',
    params,
  });
}; 